import {
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type KeyboardEventHandler,
  type FocusEvent,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { InputWrapper, type BaseInputProps } from './InputWrapper';
import { optionMatcher } from './optionMatcher';
import { cn } from '~/utils';
import { useTranslation } from 'react-i18next';

type Option = { label: string; value: string };

type MultiSelectProps = Omit<
  BaseInputProps,
  'value' | 'onChange' | 'onKeyDown' | 'size'
> & {
  options: Option[];
  value?: string[];
  onChange?: (name: string, values: string[]) => void;
  placeholder?: string;
  loading?: boolean;
  maxPanelHeight?: number;
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
  searchable?: boolean;
  alwaysShowPlaceholder?: boolean;
  testId?: string;
  /** Trigger padding. `cn` only joins, so passing padding via className cannot win here. */
  size?: 'sm' | 'md';
  /** Names the control when the layout has no room for a visible `label`. */
  ariaLabel?: string;
  /** Points at the element listing what is currently selected. */
  ariaDescribedBy?: string;
  /**
   * Lays the panel out in the flow rather than floating it over the following content, so an
   * ancestor sized to its content grows to fit the options instead of clipping them. Needed
   * inside a scrollport; elsewhere the floating panel keeps the surrounding layout still.
   */
  inFlowPanel?: boolean;
};

const TRIGGER_PADDING = { sm: 'py-2 px-3', md: 'py-3 px-4' } as const;

export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      label,
      name,
      value,
      onChange,
      options,
      disabled,
      error,
      required,
      containerClassName,
      className,
      leftIcon,
      rightIcon,
      dataCy,
      placeholder = 'Select…',
      loading = false,
      maxPanelHeight = 240,
      onFocus,
      onKeyDown,
      searchable = false,
      alwaysShowPlaceholder = false,
      testId,
      ariaLabel,
      ariaDescribedBy,
      inFlowPanel = false,
      size = 'md',
    },
    ref
  ) => {
    const { t } = useTranslation('general');
    const id = useId();
    const errorId = `${id}-error`;
    const [open, setOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    // Refs for DOM boundary checks
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement);

    const selected = useMemo(() => value ?? [], [value]);
    const selectedSet = useMemo(() => new Set(selected), [selected]);
    const selectedCount = selected.length;

    const [query, setQuery] = useState('');
    // Roving focus: exactly one option is tabbable, so Tab leaves the listbox instead of
    // walking every option, and Arrow/Home/End move within it per the ARIA listbox pattern.
    const [focusedOptionIndex, setFocusedOptionIndex] = useState(0);
    const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

    const visibleOptions = useMemo(() => {
      if (!searchable) return options;
      return options.filter(optionMatcher(query));
    }, [options, query, searchable]);

    // Clamped rather than reset in an effect: filtering can shrink the list under the focused
    // index, and a setState there would cascade a second render for derived state.
    const activeOptionIndex = Math.min(
      focusedOptionIndex,
      Math.max(0, visibleOptions.length - 1)
    );

    const selectedCountLabel = t('multi_select.selected_count', {
      selected: selectedCount,
    });

    const selectedOptions = useMemo(
      () => options.filter((o) => selectedSet.has(o.value)),
      [options, selectedSet]
    );

    const namedCount = selectedOptions.length;

    // Falls back to the count whenever the names aren't all resolvable — `options` can still
    // be loading while `value` already holds an applied filter, and rendering the placeholder
    // there would tell the buyer nothing is filtered while the list behind it is.
    const canNameSelection = namedCount === selectedCount && namedCount <= 3;

    const triggerLabel =
      alwaysShowPlaceholder || selectedCount === 0
        ? placeholder
        : canNameSelection
          ? selectedOptions.map((o) => o.label).join(', ')
          : selectedCountLabel;

    useLayoutEffect(() => {
      if (!open || !inFlowPanel) return;
      const panel = panelRef.current;
      if (typeof panel?.scrollIntoView !== 'function') return;
      panel.scrollIntoView({ block: 'nearest' });
    }, [open, inFlowPanel]);

    // Unified Click-Outside & Focus Cleanup
    useEffect(() => {
      if (!open && !isFocused) return;

      const onDocClick = (e: MouseEvent) => {
        const target = e.target as Node;
        // If clicking outside the entire component boundary
        if (containerRef.current && !containerRef.current.contains(target)) {
          setOpen(false);
          setIsFocused(false);
          setQuery('');
        }
      };

      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [open, isFocused]);

    const toggle = () => {
      if (disabled) return;
      const nextState = !open;
      setOpen(nextState);
      setIsFocused(true); // Keep ring active when interacting
      if (nextState) onFocus?.();
      else setQuery('');
    };

    const commitChange = (next: string[]) => {
      onChange?.(name, next);
    };

    const onCheck = (checked: boolean, v: string) => {
      const next = new Set(selected);
      if (checked) next.add(v);
      else next.delete(v);
      commitChange(Array.from(next));
    };

    const handleBlur = (e: FocusEvent) => {
      // Check if the new focus target is outside the container
      if (!containerRef.current?.contains(e.relatedTarget as Node)) {
        setIsFocused(false);
        setOpen(false);
        setQuery('');
      }
    };

    const focusOption = (index: number) => {
      setFocusedOptionIndex(index);
      optionRefs.current[index]?.focus();
    };

    const handleOptionKeyDown = (
      event: ReactKeyboardEvent<HTMLLIElement>,
      index: number,
      option: Option,
      checked: boolean
    ) => {
      const count = visibleOptions.length;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        focusOption((index + step + count) % count);
        return;
      }
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        focusOption(event.key === 'Home' ? 0 : count - 1);
        return;
      }
      if (event.key !== ' ' && event.key !== 'Enter') return;
      event.preventDefault();
      onCheck(!checked, option.value);
    };

    const clearAll = () => commitChange([]);

    return (
      <InputWrapper
        label={label}
        id={id}
        errorId={errorId}
        error={error}
        required={required}
        disabled={disabled}
        containerClassName={containerClassName}
        // Sync the visual ring with our internal state
        focus={isFocused || open}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
      >
        {/* 1. Relative anchor for the absolute panel */}
        <div className="relative w-full" ref={containerRef} onBlur={handleBlur}>
          {/* Trigger */}
          <button
            type="button"
            id={id}
            ref={triggerRef}
            name={name}
            className={cn(
              'bg-none border-none focus:outline-none hover:outline-none text-sm leading-[100%] bg-neut-0 rounded font-normal w-full text-left',
              TRIGGER_PADDING[size],
              'flex items-center justify-between',
              className
            )}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={`${id}-panel`}
            aria-label={ariaLabel}
            aria-describedby={
              [error ? errorId : undefined, ariaDescribedBy]
                .filter(Boolean)
                .join(' ') || undefined
            }
            aria-disabled={disabled}
            disabled={disabled}
            onClick={toggle}
            onFocus={() => setIsFocused(true)}
            onKeyDown={onKeyDown}
            data-cy={dataCy}
            data-testid={testId}
          >
            {/* alwaysShowPlaceholder makes the placeholder a standing label rather than an
                empty value, so it keeps normal text colour whatever is selected. */}
            <span
              className={cn(
                !alwaysShowPlaceholder && selectedCount === 0
                  ? 'text-neut-400'
                  : ''
              )}
            >
              {loading ? t('multi_select.loading') : triggerLabel}
            </span>
            <svg
              className={cn(
                'shrink-0 ml-2 h-4 w-4 text-neut-500 transition-transform',
                open ? 'rotate-180' : undefined // Returns undefined instead of false
              )}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.186l3.71-3.955a.75.75 0 111.08 1.04l-4.24 4.52a.75.75 0 01-1.08 0l-4.24-4.52a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Popover panel */}
          {open && (
            <div
              id={`${id}-panel`}
              data-testid={testId ? `${testId}-panel` : undefined}
              ref={panelRef}
              role="listbox"
              aria-multiselectable="true"
              className={cn(
                'z-50 w-full bg-white flex flex-col overflow-hidden',
                inFlowPanel
                  ? 'border-t border-neut-border'
                  : 'absolute min-w-56 left-0 top-full mt-1 rounded border border-neut-border shadow-md'
              )}
              style={{
                maxHeight: Math.max(160, maxPanelHeight),
              }}
              // Prevent clicks inside panel from triggering unintended parent behaviors
              onMouseDown={(e) => e.stopPropagation()}
            >
              {/* Controls */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-neut-border">
                <div className="text-xs text-neut-500">
                  {selectedCountLabel}
                </div>
                <div className="space-x-2">
                  <button
                    type="button"
                    className="text-xs text-neut-600 hover:text-neut-900"
                    onClick={clearAll}
                  >
                    {t('multi_select.clear')}
                  </button>
                  <button
                    type="button"
                    className="text-xs text-prim-600 hover:text-prim-800 font-bold"
                    onClick={() => {
                      setOpen(false);
                      setIsFocused(false);
                      setQuery('');
                      // This button unmounts with the panel, so focus would fall to the body.
                      triggerRef.current?.focus();
                    }}
                  >
                    {t('multi_select.apply')}
                  </button>
                </div>
              </div>

              {searchable && (
                <div className="px-3 py-2 border-b border-neut-border">
                  <input
                    type="text"
                    className="w-full rounded border border-neut-border px-2 py-1 text-sm"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={t('searchable_select.search_placeholder')}
                    aria-label={t('searchable_select.search_options_label')}
                  />
                </div>
              )}

              {/* Options list */}
              <div className="flex-1 min-h-0 overflow-auto py-1">
                {visibleOptions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-neut-500">
                    {loading
                      ? t('multi_select.loading')
                      : searchable && query.trim()
                        ? t('searchable_select.no_results')
                        : t('multi_select.no_options')}
                  </div>
                ) : (
                  <ul className="py-1">
                    {visibleOptions.map((opt, index) => {
                      const checked = selectedSet.has(opt.value);
                      return (
                        <li
                          key={opt.value}
                          role="option"
                          aria-selected={checked}
                          ref={(element) => {
                            optionRefs.current[index] = element;
                          }}
                          tabIndex={index === activeOptionIndex ? 0 : -1}
                          className="px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-neut-50 focus:bg-neut-50 focus:outline-none"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => onCheck(!checked, opt.value)}
                          onFocus={() => setFocusedOptionIndex(index)}
                          onKeyDown={(event) =>
                            handleOptionKeyDown(event, index, opt, checked)
                          }
                        >
                          {/* Presentational: the row is the control, so a focusable input
                              here would be a duplicate tab stop inside role="option". */}
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-prim pointer-events-none"
                            checked={checked}
                            readOnly
                            tabIndex={-1}
                            aria-hidden="true"
                          />
                          <span className="text-sm w-full pointer-events-none">
                            {opt.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </InputWrapper>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';
